import { useMarkets } from '@api/useMarkets'
import {
  validateBreakMinutes,
  validateLat,
  validateLon,
  validatePostTripMinutes,
  validatePreTripMinutes,
  validateServiceRadius,
  validateShiftLength,
} from '@components/_SiteWide/CellTemplates/validators'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { stateAbbreviations } from '@constants/stateAbbreviations'
import { GraviGrid, Texto, useGridFetch } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import api from '@utils/api'
import { getStoreNumberComparator, gridViewManager } from '@utils/grid'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { TimePicker } from 'antd'
import moment from 'moment'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { BasicSelectEditor } from '@/components/_SiteWide/Grid/CellEditors/BasicSelectEditor'

import { depotCreateConfig } from './depotCreateConfig'
import { ampmTimeDisabler } from './util'

const STORAGE_KEY = 'depot'

export function Depot() {
  const { rowData, isLoading, refresh } = useGridFetch(api.getDepots)
  const [marketOptions, setMarketOptions] = useState([])
  const [sectorOptions, setSectorOptions] = useState([])
  const { useMarketsQuery } = useMarkets()
  const { data: marketData } = useMarketsQuery()
  const { updateSetting } = useUserSettings()
  const gridApiRef = useRef()
  useEffect(() => {
    if (marketData) {
      setMarketOptions(marketData?.rows?.map((r) => r.name))

      const allSectors = new Set()
      marketData?.rows?.forEach((market) => {
        market.sectors?.forEach((sector) => {
          allSectors.add(sector.name)
        })
      })
      setSectorOptions(Array.from(allSectors).sort())
    }
  }, [marketData])
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.location_write

  const handleUpdate = useCallback(async (values) => {
    const res = await api.updateDepot(values)

    const updatedData = res.data || res
    if (updatedData.market !== undefined) {
      values.market = updatedData.market
    }

    const node = gridApiRef?.current?.getRowNode(updatedData.id || values.id)
    if (node) {
      gridApiRef.current.refreshCells({ rowNodes: [node], force: true })
    }

    return res
  }, [])
  const columnDefs = useMemo(
    () => getColumnDefs({ stateOptions: stateAbbreviations, marketOptions, sectorOptions }),
    [marketOptions, sectorOptions]
  )
  const getRowId = useCallback((row) => row?.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      frameworkComponents: { shiftTimeEditor: ShiftTimeEditor },
    }),
    [getRowId]
  )

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Depot Management',
        actionButtons: (
          <UploadDownloadButtons
            refreshCallback={refresh}
            downloadEP={api.downloadDepots}
            uploadEP={canEdit && api.uploadDepots}
            fileName='Depots.xlsx'
          />
        ),
      }}
      externalRef={gridApiRef}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      storageKey={STORAGE_KEY}
      loading={isLoading}
      rowData={rowData}
      shouldInsertCreated
      updateEP={canEdit && handleUpdate}
      createEP={canEdit && api.createDepot}
      createConfig={depotCreateConfig}
      createSelectOptions={{ stateAbbreviations, marketOptions, sectorOptions }}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

export const ShiftTimeEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value && moment(props.value))

  const { type } = props
  useImperativeHandle(ref, () => ({
    getValue: () => value.toISOString(),
    isPopup: () => false,
    isCancelBeforeStart: () => false,
    isCancelAfterEnd: () => false,
  }))

  return (
    <TimePicker
      showNow={false}
      onChange={(moment) => setValue(moment)}
      value={value}
      style={{ width: '100%' }}
      minuteStep={15}
      format='h:mm a'
      disabledTime={() => ampmTimeDisabler(type)}
    />
  )
})

function ShiftTimeRenderer({ data, type, timeZone }) {
  if (type === 'am') {
    if (data?.am_shift_start_time) {
      return (
        <Texto appearance='secondary' weight='bold'>
          {formatters.TIME(moment(data?.am_shift_start_time), timeZone)}
        </Texto>
      )
    }
    return (
      <Texto weight={600} appearance='medium'>
        {formatters.TIME(moment(data?.am_shift?.start_time), timeZone)} (System)
      </Texto>
    )
  }
  if (type === 'pm') {
    if (data?.pm_shift_start_time) {
      return (
        <Texto appearance='secondary' weight='bold'>
          {formatters.TIME(moment(data?.pm_shift_start_time), timeZone)}
        </Texto>
      )
    }
    return (
      <Texto weight={600} appearance='medium'>
        {formatters.TIME(moment(data?.pm_shift?.start_time), timeZone)} (System)
      </Texto>
    )
  }
}

function ShiftLengthRenderer({ data }) {
  if (!data.shift_length) return null
  if (data.shift_length.type === 'depot') {
    return (
      <Texto appearance='secondary' weight='bold' align='right'>
        {data.shift_length.shift_length_hours}
      </Texto>
    )
  }
  return (
    <Texto weight={600} appearance='medium' align='right'>
      {data.shift_length.shift_length_hours} (System)
    </Texto>
  )
}

const getColumnDefs = (selectedOptions) => [
  {
    field: 'name',
    filter: true,
  },
  {
    field: 'supervisor',
    filter: true,
  },
  {
    field: 'market',
    filter: true,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    cellEditorParams: {
      values: selectedOptions.marketOptions,
    },
  },
  {
    field: 'sector',
    filter: true,
    cellEditor: forwardRef((params, ref) => {
      const options =
        selectedOptions?.sectorOptions
          .map((x) => ({ value: x, label: x }))
          ?.sort((a, b) => a.label.localeCompare(b.label)) ?? []
      return <BasicSelectEditor {...params} showSearch ref={ref} options={options} placeholder='Select Sector' />
    }),
  },
  {
    field: 'address',
    filter: true,
  },
  {
    field: 'city',
    filter: true,
  },
  {
    field: 'state',
    filter: true,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    cellEditorParams: {
      values: selectedOptions.stateOptions,
    },
  },
  {
    headerName: 'Postal Code',
    field: 'postal_code',
    filter: true,
    comparator: getStoreNumberComparator('postal_code'),
  },
  {
    field: 'latitude',
    sortable: false,
    cellClass: 'right-align-cell',
    flex: 2,
    valueSetter: validateLat,
    type: 'rightAligned',
  },
  {
    field: 'longitude',
    sortable: false,
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    flex: 2,
    valueSetter: validateLon,
  },
  {
    field: 'am_shift_start_time',
    headerName: 'AM Shift Start Time',
    cellRenderer: (params) => (
      <ShiftTimeRenderer data={params.data} type='am' timeZone={params.data.am_shift_start_time_local} />
    ),
    cellRendererParams: { type: 'am' },
    cellEditor: 'shiftTimeEditor',
    cellEditorParams: { type: 'am' },
    filter: false,
  },
  {
    field: 'pm_shift_start_time',
    headerName: 'PM Shift Start Time',
    cellRenderer: (params) => (
      <ShiftTimeRenderer data={params.data} type='pm' timeZone={params.data.pm_shift_start_time_local} />
    ),
    cellRendererParams: { type: 'pm' },
    cellEditor: 'shiftTimeEditor',
    cellEditorParams: { type: 'pm' },
    filter: false,
  },
  {
    field: 'shift_length_hours',
    headerName: 'Shift Length Hours',
    headerTooltip: 'Applies to both AM and PM shifts in .5h increments',
    valueGetter: ({ data }) => data.shift_length?.shift_length_hours,
    sortable: true,
    filter: 'agNumberColumnFilter',
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    cellRenderer: (params) => <ShiftLengthRenderer data={params.data} />,
    valueSetter: validateShiftLength,
  },
  {
    field: 'service_radius',
    headerName: 'Service Radius',
    sortable: true,
    filter: 'agNumberColumnFilter',
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    valueSetter: validateServiceRadius,
  },
  {
    field: 'pre_trip_minutes',
    headerName: 'Pre Trip Minutes',
    sortable: true,
    filter: 'agNumberColumnFilter',
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    valueSetter: validatePreTripMinutes,
  },
  {
    field: 'post_trip_minutes',
    headerName: 'Post Trip Minutes',
    sortable: true,
    filter: 'agNumberColumnFilter',
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    valueSetter: validatePostTripMinutes,
  },
  {
    field: 'break_minutes',
    headerName: 'Break Minutes',
    sortable: true,
    filter: 'agNumberColumnFilter',
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    valueSetter: validateBreakMinutes,
  },
]
